import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Eye, Search, Filter, RefreshCw, Calendar, FileText, Plus, TrendingUp,
  Clock, CheckCircle, XCircle, AlertCircle, X, Video, Copy, LogOut
} from 'lucide-react';
import api, { deleteJobApplication } from '../../api/client';

// Reusable Components
import Modal from '../common/Modal';
import PageHeader, { PageCard, PageShell } from '../common/PageHeader';
import SortableHeader from '../common/SortableHeader';
import PremiumSelect from '../admin/component/PremiumSelect';
import { theme } from '../../theme.js';

// =============================================================
// GLOBAL HELPERS (Correct position — before any component usage)
// =============================================================
const getInterviewBaseUrl = () =>
  process.env.REACT_APP_INTERVIEW_UI_URL || window.location.origin;

const resolveInterviewUrl = (rawUrl) => {
  if (!rawUrl) return '';
  if (rawUrl.startsWith('http')) return rawUrl;
  return `${getInterviewBaseUrl()}${rawUrl}`;
};

// =============================================================
// Toast Component
// =============================================================
const Toast = ({ show, message, type, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 6000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  const Icon = type === 'success' ? CheckCircle : XCircle;
  const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';

  return (
    <div className="fixed top-4 right-4 z-[9999]">
      <div className={`${bgColor} text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3`}>
        <Icon className="w-5 h-5" />
        <span>{message}</span>
        <button onClick={onClose} className="ml-4 hover:text-gray-200">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, color, icon, onClick, isActive }) => (
  <div
    onClick={onClick}
    className={`bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border ${isActive ? 'ring-2 ring-indigo-500 border-indigo-400 shadow-indigo-100' : 'border-white/60'
      } group hover:-translate-y-1 ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="mb-1 text-gray-600 font-medium">{title}</p>
        <p className={`text-3xl font-bold ${color}`}>{value}</p>
      </div>
      <div className={`p-3 rounded-2xl ${color.includes('amber') ? 'bg-amber-100'
        : color.includes('green') ? 'bg-green-100'
          : color.includes('red') ? 'bg-red-100'
            : 'bg-blue-100'
        } group-hover:scale-110 transition-transform duration-300`}>
        {icon}
      </div>
    </div>
  </div>
);

// Constants
const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  reviewed: 'bg-blue-100 text-blue-800 border-blue-200',
  shortlisted: 'bg-green-100 text-green-800 border-green-200',
  interview_scheduled: 'bg-purple-100 text-purple-800 border-purple-200',
  interviewed: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  accepted: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  withdrawn: 'bg-gray-100 text-gray-800 border-gray-200'
};

const STATUS_ICONS = {
  pending: Clock,
  reviewed: Eye,
  shortlisted: TrendingUp,
  interview_scheduled: Calendar,
  interviewed: FileText,
  accepted: CheckCircle,
  rejected: XCircle,
  withdrawn: AlertCircle
};

// =============================================================
// MAIN COMPONENT
// =============================================================
export default function MyApplications({ adminToken, onLogout }) {
  const { t } = useTranslation();
  // const navigate = useNavigate(); // Removed unused
  // const { logout } = useContext(AuthContext); // Removed unused

  const [applications, setApplications] = useState([]);
  const [, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingApplication, setDeletingApplication] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [interviewLinks, setInterviewLinks] = useState({});
  const [interviewStatus, setInterviewStatus] = useState({});
  const [stats, setStats] = useState({ total: 0, pending: 0, shortlisted: 0, rejected: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [creatingInterview, setCreatingInterview] = useState(null);
  const [sortKey, setSortKey] = useState('applied_at');
  const [sortDirection] = useState('desc');

  useEffect(() => {
    fetchApplications();
    fetchStatistics();
    fetchInterviewLinks();
    fetchInterviewStatus();
  },// eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  const fetchApplications = async (nextStatusFilter = statusFilter, nextSearchTerm = searchTerm) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (nextStatusFilter) params.append('status', nextStatusFilter);
      if (nextSearchTerm) params.append('search', nextSearchTerm);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`job-applications/my_applications/${queryString}`);
      setApplications(response.data.results || response.data);
    } catch (err) {
      setError(t('myApplications.failedToLoadApplications'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('job-applications/my_applications/');
      const data = response.data.results || response.data;
      const statusCounts = data.reduce((acc, app) => {
        acc[app.status] = (acc[app.status] || 0) + 1;
        return acc;
      }, {});
      setStats({
        total: data.length,
        pending: statusCounts.pending || 0,
        shortlisted: statusCounts.shortlisted || 0,
        rejected: statusCounts.rejected || 0
      });
    } catch (err) { }
  };

  const fetchInterviewLinks = async () => {
    try {
      const response = await api.get('job-applications/my_interview_links/');
      const backendLinks = response.data.interview_links.reduce((acc, link) => {
        acc[link.application_id] = {
          url: link.interview_link,
          jobTitle: link.job_title,
          companyName: link.company_name,
          createdAt: link.created_at,
          expiresAt: link.expires_at
        };
        return acc;
      }, {});
      const localLinks = JSON.parse(localStorage.getItem('generatedInterviewLinks') || '{}');
      setInterviewLinks({ ...localLinks, ...backendLinks });
    } catch (err) {
      const localLinks = JSON.parse(localStorage.getItem('generatedInterviewLinks') || '{}');
      setInterviewLinks(localLinks);
    }
  };

  const fetchInterviewStatus = async () => {
    try {
      const response = await api.get('job-applications/my_interview_status/');
      const statusMap = response.data.interview_status.reduce((acc, status) => {
        acc[status.application_id] = status;
        return acc;
      }, {});
      setInterviewStatus(statusMap);
    } catch (err) { }
  };

  const createInterviewSession = async (applicationId) => {
    try {
      setCreatingInterview(applicationId);
      const response = await api.post(`job-applications/${applicationId}/create_interview_session/`);
      const app = applications.find(a => a.id === applicationId);

      setInterviewLinks(prev => ({
        ...prev,
        [applicationId]: {
          url: response.data.invite_url,
          jobTitle: app?.job_title,
          companyName: app?.company_name,
          createdAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        }
      }));

      setSnackbar({ open: true, message: t('myApplications.interviewSessionCreated'), severity: 'success' });
      fetchInterviewStatus();
    } catch (err) {
      setSnackbar({ open: true, message: t('myApplications.failedToCreateInterview'), severity: 'error' });
    } finally {
      setCreatingInterview(null);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteJobApplication(deletingApplication.id);
      setSnackbar({ open: true, message: t('myApplications.applicationWithdrawn'), severity: 'success' });
      fetchApplications();
      fetchStatistics();
      setDeleteDialogOpen(false);
      setDeletingApplication(null);
    } catch (error) {
      setSnackbar({ open: true, message: t('myApplications.failedToWithdraw'), severity: 'error' });
    }
  };

  const copyInterviewLink = async (linkUrl, jobTitle) => {
    try {
      if (!linkUrl) {
        setSnackbar({ open: true, message: t('myApplications.noLinkToCopy', { title: jobTitle }), severity: 'error' });
        return;
      }

      const fullUrl = resolveInterviewUrl(linkUrl);
      await navigator.clipboard.writeText(fullUrl);

      setSnackbar({ open: true, message: t('myApplications.linkCopied', { title: jobTitle }), severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: t('myApplications.failedToCopyLink'), severity: 'error' });
    }
  };

  const sortedApplications = useMemo(() => {
    const normalizedSearch = (searchTerm || '').trim().toLowerCase();

    const locallyFiltered = applications.filter((app) => {
      // 1. Status Filter
      if (statusFilter && app.status !== statusFilter) {
        return false;
      }

      // 2. Search Filter
      if (!normalizedSearch) return true;
      const jobTitle = (app.job_title || '').toLowerCase();
      const companyName = (app.company_name || '').toLowerCase();
      return jobTitle.includes(normalizedSearch) || companyName.includes(normalizedSearch);
    });

    return [...locallyFiltered].sort((a, b) => {
      let aVal, bVal;
      switch (sortKey) {
        case 'job_title':
        case 'company_name':
        case 'status':
          aVal = (a[sortKey] || '').toLowerCase();
          bVal = (b[sortKey] || '').toLowerCase();
          break;
        case 'applied_at':
          aVal = new Date(a.applied_at || 0);
          bVal = new Date(b.applied_at || 0);
          break;
        case 'expected_salary':
          aVal = a.expected_salary || 0;
          bVal = b.expected_salary || 0;
          break;
        default:
          aVal = a[sortKey] || '';
          bVal = b[sortKey] || '';
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [applications, sortKey, sortDirection, searchTerm, statusFilter]);

  const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.withdrawn;
  const getStatusIcon = (status) => {
    const Icon = STATUS_ICONS[status] || Clock;
    return <Icon className="w-4 h-4" />;
  };
  const formatDate = (dateString) => dateString
    ? new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : 'N/A';
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    return (
      d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) +
      ' at ' +
      d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    );
  };

  const formatDuration = (att) => {
    if (att?.started_at && att?.ended_at) {
      const diffMs = new Date(att.ended_at) - new Date(att.started_at);
      if (diffMs > 0) {
        const totalSecs = Math.round(diffMs / 1000);
        const mins = Math.floor(totalSecs / 60);
        const secs = totalSecs % 60;
        if (mins > 0 && secs > 0) return `${mins}m ${secs}s`;
        if (mins > 0) return `${mins} min${mins > 1 ? 's' : ''}`;
        return `${secs}s`;
      }
    }
    if (att?.duration_minutes) {
      return `${att.duration_minutes} min${att.duration_minutes > 1 ? 's' : ''}`;
    }
    return null;
  };

  const formatSalary = (salary) => salary ? `$${Number(salary).toLocaleString()}` : t('myApplications.notSpecified');

  const formatAttemptLabel = (num) => {
    const keyMap = {
      1: 'firstAttempt',
      2: 'secondAttempt',
      3: 'thirdAttempt',
      4: 'fourthAttempt',
      5: 'fifthAttempt'
    };
    const defaultMap = {
      1: 'First Attempt',
      2: 'Second Attempt',
      3: 'Third Attempt',
      4: 'Fourth Attempt',
      5: 'Fifth Attempt'
    };
    if (keyMap[num]) {
      return t(`myApplications.${keyMap[num]}`, defaultMap[num]);
    }
    return `${t('myApplications.attempt', 'Attempt')} ${num}`;
  };

  const InterviewStatusCell = ({ application }) => {
    const link = interviewLinks[application.id];
    const status = interviewStatus[application.id];
    const isWithdrawn = application.status === 'withdrawn';

    if (isWithdrawn) return null;

    if (link?.url) {
      const isExpired = new Date(link.expiresAt) < new Date();
      return (
        <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs ${isExpired ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
          : 'bg-green-100 text-green-800 border-green-200'
          }`}>
          <div className={`w-2 h-2 rounded-full ${isExpired ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'
            }`}></div>

          <a
            href={resolveInterviewUrl(link.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate"
          >
            {isExpired ? t('myApplications.linkExpired') : t('myApplications.ready')}
          </a>

          <button
            onClick={() => copyInterviewLink(link.url, application.job_title)}
            className="p-1 text-indigo-600 hover:text-indigo-800 rounded"
          >
            <Copy className="w-3 h-3" />
          </button>
        </div>
      );
    }

    if (status?.has_completed_interview) {
      return (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs font-semibold">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
          <span>{t('myApplications.completed', 'Completed')}</span>
        </div>
      );
    }

    if (status?.has_active_interview) {
      return (
        <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-purple-100 text-purple-800 border border-purple-200 text-xs">
          <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
          <span>{t('myApplications.liveInterview')}</span>
        </div>
      );
    }

    return (
      <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-xs">
        <Clock className="w-3 h-3" />
        <span>{t('myApplications.noInterview')}</span>
      </div>
    );
  };

  return (
    <PageShell fill={false} className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50" style={{ fontFamily: theme.fonts.body }}>
      <PageCard overflow={false}>
          <PageHeader
            title={t('myApplications.pageTitle')}
            subtitle={t('myApplications.pageSubtitle')}
          />
        <div className="p-3 sm:p-4">

        {/* STATS GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title={t('myApplications.totalApplications')}
            value={stats.total}
            color="text-blue-600"
            icon={<FileText className="w-6 h-6 text-blue-600" />}
            isActive={statusFilter === ''}
            onClick={() => {
              setStatusFilter('');
              fetchApplications('', searchTerm);
            }}
          />
          <StatCard
            title={t('myApplications.pendingReview')}
            value={stats.pending}
            color="text-amber-600"
            icon={<Clock className="w-6 h-6 text-amber-600" />}
            isActive={statusFilter === 'pending'}
            onClick={() => {
              const newStatus = statusFilter === 'pending' ? '' : 'pending';
              setStatusFilter(newStatus);
              fetchApplications(newStatus, searchTerm);
            }}
          />
          <StatCard
            title={t('myApplications.shortlisted')}
            value={stats.shortlisted}
            color="text-green-600"
            icon={<TrendingUp className="w-6 h-6 text-green-600" />}
            isActive={statusFilter === 'shortlisted'}
            onClick={() => {
              const newStatus = statusFilter === 'shortlisted' ? '' : 'shortlisted';
              setStatusFilter(newStatus);
              fetchApplications(newStatus, searchTerm);
            }}
          />
          <StatCard
            title={t('myApplications.rejected')}
            value={stats.rejected}
            color="text-red-600"
            icon={<XCircle className="w-6 h-6 text-red-600" />}
            isActive={statusFilter === 'rejected'}
            onClick={() => {
              const newStatus = statusFilter === 'rejected' ? '' : 'rejected';
              setStatusFilter(newStatus);
              fetchApplications(newStatus, searchTerm);
            }}
          />
        </div>

        {/* FILTERS */}
        <div className="relative z-20 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-8 border border-white/60">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 items-end">
            <div className="lg:col-span-2">
              <label className="block text-slate-700 mb-2 font-medium">{t('myApplications.searchApplications')}</label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('myApplications.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      fetchApplications(statusFilter, searchTerm);
                    }
                  }}
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 bg-white/70"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-700 mb-2 font-medium">{t('myApplications.filterByStatus')}</label>
              <PremiumSelect
                variant="b2c"
                value={statusFilter}
                onChange={(newStatus) => {
                  setStatusFilter(newStatus);
                  fetchApplications(newStatus, searchTerm);
                }}
                placeholder={t('myApplications.allStatuses')}
                searchable={false}
                options={[
                  { value: '', label: t('myApplications.allStatuses') },
                  { value: 'pending', label: t('myApplications.pending') },
                  { value: 'reviewed', label: t('myApplications.reviewed') },
                  { value: 'shortlisted', label: t('myApplications.shortlistedOption') },
                  { value: 'interview_scheduled', label: t('myApplications.interviewScheduled') },
                  { value: 'interviewed', label: t('myApplications.interviewed') },
                  { value: 'accepted', label: t('myApplications.accepted') },
                  { value: 'rejected', label: t('myApplications.rejectedOption') },
                  { value: 'withdrawn', label: t('myApplications.withdrawn') },
                ]}
              />
            </div>

            <div className="md:col-span-2 flex gap-3">
              <button
                onClick={() => {
                  fetchApplications(statusFilter, searchTerm);
                }}
                className="flex items-center justify-center gap-2 w-full md:w-auto px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl shadow-md hover:bg-indigo-700 transition-all"
              >
                <Filter className="w-4 h-4" />
                <span>{t('myApplications.applyFilters')}</span>
              </button>
              <button
                onClick={() => {
                  setStatusFilter('');
                  setSearchTerm('');
                  fetchApplications('', '');
                }}
                className="flex items-center justify-center gap-2 w-full md:w-auto px-6 py-3 border border-gray-300 hover:bg-gray-100 text-gray-700 font-semibold rounded-xl transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                <span>{t('myApplications.clear')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* ERROR BOX */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-6 mb-8 shadow-md">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-500 mr-3" />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* MOBILE CARDS */}
        <div className="lg:hidden space-y-4">
          {sortedApplications.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center text-gray-500 border">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-base font-semibold text-gray-700">
                {t('myApplications.noApplicationsFound', 'No applications found')}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {statusFilter || searchTerm
                  ? t('myApplications.tryAdjustingFilters', 'Try adjusting your filters or search terms.')
                  : t('myApplications.noApplicationsYet', 'You have not submitted any applications yet.')}
              </p>
              {(statusFilter || searchTerm) && (
                <button
                  onClick={() => {
                    setStatusFilter('');
                    setSearchTerm('');
                    fetchApplications('', '');
                  }}
                  className="mt-3 inline-block text-xs font-semibold text-indigo-600 hover:text-indigo-800 underline"
                >
                  {t('myApplications.clearFilters', 'Clear all filters')}
                </button>
              )}
            </div>
          ) : (
            sortedApplications.map((app) => (
              <div key={app.id} className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    {app.job_public_link ? (
                      <a
                        href={app.job_public_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-base font-semibold text-gray-900 truncate block hover:text-indigo-600"
                      >
                        {app.job_title}
                      </a>
                    ) : (
                      <div className="text-base font-semibold text-gray-900 truncate">{app.job_title}</div>
                    )}
                    <div className="text-sm text-gray-500 truncate">{app.company_name}</div>
                  </div>

                  <div className="text-right min-w-[90px]">
                    <div className="text-xs text-gray-500">{t('myApplications.applied')}</div>
                    <div className="text-sm font-medium text-gray-800">{formatDate(app.applied_at)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('myApplications.statusLabel')}</div>
                    <span className={`inline-flex items-center gap-2 px-2 py-1 rounded-full border text-xs ${getStatusColor(app.status)}`}>
                      {getStatusIcon(app.status)}
                      <span className="truncate">{app.status_display}</span>
                    </span>
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-gray-500 mb-1">{t('myApplications.interview')}</div>
                    <InterviewStatusCell application={app} />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    onClick={() => { setSelectedApplication(app); setDetailDialogOpen(true); }}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                  >
                    <Eye className="w-5 h-5" />
                  </button>

                  <button
                    onClick={() => createInterviewSession(app.id)}
                    disabled={creatingInterview === app.id}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                  >
                    {creatingInterview === app.id ? (
                      <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Plus className="w-5 h-5" />
                    )}
                  </button>

                  <button
                    onClick={() => { setDeletingApplication(app); setDeleteDialogOpen(true); }}
                    className="p-2 text-red-500 hover:bg-red-100 rounded-lg"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* DESKTOP TABLE */}
        <div className="hidden lg:block bg-white rounded-xl shadow-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b text-gray-600">
                <tr>
                  <SortableHeader sortKey="job_title" currentSortKey={sortKey} sortDirection={sortDirection} onSort={setSortKey} width="18%">{t('myApplications.jobPosition')}</SortableHeader>
                  <SortableHeader sortKey="company_name" currentSortKey={sortKey} sortDirection={sortDirection} onSort={setSortKey} width="14%">{t('myApplications.company')}</SortableHeader>
                  <SortableHeader sortKey="status" currentSortKey={sortKey} sortDirection={sortDirection} onSort={setSortKey} width="12%">{t('myApplications.statusLabel')}</SortableHeader>
                  <SortableHeader sortKey="applied_at" currentSortKey={sortKey} sortDirection={sortDirection} onSort={setSortKey} width="14%">{t('myApplications.appliedDate')}</SortableHeader>
                  <SortableHeader sortKey="expected_salary" currentSortKey={sortKey} sortDirection={sortDirection} onSort={setSortKey} width="14%">{t('myApplications.expectedSalary')}</SortableHeader>
                  <th className="text-center py-3 px-4 font-medium">{t('myApplications.interview')}</th>
                  <th className="text-center py-3 px-4 font-medium">{t('myApplications.actions')}</th>
                </tr>
              </thead>

              <tbody className="text-gray-700">
                {sortedApplications.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <FileText className="w-10 h-10 text-gray-300" />
                        <p className="text-base font-semibold text-gray-700">
                          {t('myApplications.noApplicationsFound', 'No applications found')}
                        </p>
                        <p className="text-xs text-gray-400">
                          {statusFilter || searchTerm
                            ? t('myApplications.tryAdjustingFilters', 'Try adjusting your filters or search terms.')
                            : t('myApplications.noApplicationsYet', 'You have not submitted any applications yet.')}
                        </p>
                        {(statusFilter || searchTerm) && (
                          <button
                            onClick={() => {
                              setStatusFilter('');
                              setSearchTerm('');
                              fetchApplications('', '');
                            }}
                            className="mt-2 text-xs font-semibold text-indigo-600 hover:text-indigo-800 underline"
                          >
                            {t('myApplications.clearFilters', 'Clear all filters')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedApplications.map((app) => {
                    return (
                      <tr key={app.id} className="border-b last:border-b-0 hover:bg-gray-50/60 transition-colors">
                        <td className="py-3 px-4">
                          {app.job_public_link ? (
                            <a
                              href={app.job_public_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-900 font-semibold truncate block hover:text-indigo-600"
                            >
                              {app.job_title}
                            </a>
                          ) : (
                            <span className="text-gray-900 font-semibold truncate block">{app.job_title}</span>
                          )}
                        </td>

                        <td className="py-3 px-4">
                          <span className="truncate block">{app.company_name}</span>
                        </td>

                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-2 px-2 py-1 rounded-full border text-xs ${getStatusColor(app.status)}`}>
                            {getStatusIcon(app.status)}
                            <span className="truncate">{app.status_display}</span>
                          </span>
                        </td>

                        <td className="py-3 px-4">{formatDate(app.applied_at)}</td>
                        <td className="py-3 px-4">{formatSalary(app.expected_salary)}</td>

                        <td className="py-3 px-4 text-center">
                          <InterviewStatusCell application={app} />
                        </td>

                        <td className="py-3 px-4">
                          {app.status !== 'withdrawn' && (
                            <div className="flex items-center gap-2 justify-center">
                              <button
                                onClick={() => { setSelectedApplication(app); setDetailDialogOpen(true); }}
                                className="p-2 text-gray-500 hover:bg-gray-200/70 rounded-lg"
                                title={t('myApplications.viewDetails')}
                              >
                                <Eye className="w-4 h-4" />
                              </button>

                              <button
                                onClick={() => createInterviewSession(app.id)}
                                disabled={creatingInterview === app.id}
                                className="p-2 text-gray-500 hover:bg-gray-200/70 rounded-lg"
                                title={t('myApplications.createInterview')}
                              >
                                {creatingInterview === app.id ? (
                                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Plus className="w-4 h-4" />
                                )}
                              </button>

                              <button
                                onClick={() => { setDeletingApplication(app); setDeleteDialogOpen(true); }}
                                className="p-2 text-red-500 hover:bg-red-100 rounded-lg"
                                title="Withdraw"
                              >
                                <LogOut className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  }))}
              </tbody>
            </table>
          </div>
        </div>

        {/* DETAILS MODAL */}
        <Modal isOpen={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="max-w-5xl">
          <div className="px-8 py-6 bg-gradient-to-r from-slate-50 to-indigo-50/30 rounded-t-3xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-indigo-600" />
                <div>
                  <h3 className="text-2xl font-bold text-slate-800">{t('myApplications.applicationDetails')}</h3>
                  <p className="text-gray-600">{selectedApplication?.job_title}</p>
                </div>
              </div>
              <button onClick={() => setDetailDialogOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-8 max-h-[60vh] overflow-y-auto">
            {selectedApplication && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <h4 className="text-xl font-bold text-slate-800">{t('myApplications.jobInformation')}</h4>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('myApplications.position')}</p>
                    <p className="text-gray-900 font-semibold">{selectedApplication.job_title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('myApplications.company')}</p>
                    <p className="text-gray-900">{selectedApplication.company_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('myApplications.statusLabel')}</p>
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm ${getStatusColor(selectedApplication.status)}`}>
                      {getStatusIcon(selectedApplication.status)}
                      {selectedApplication.status_display}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('myApplications.appliedOn')}</p>
                    <p className="text-gray-900">{formatDate(selectedApplication.applied_at)}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <h4 className="text-xl font-bold text-slate-800">{t('myApplications.applicationDetailsTitle')}</h4>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('myApplications.daysSinceApplied')}</p>
                    <p className="text-gray-900">{selectedApplication.days_since_applied} {t('myApplications.days')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('myApplications.expectedSalary')}</p>
                    <p className="text-gray-900">{formatSalary(selectedApplication.expected_salary)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('myApplications.earliestStartDate')}</p>
                    <p className="text-gray-900">
                      {selectedApplication.earliest_start_date ? formatDate(selectedApplication.earliest_start_date) : t('myApplications.notSpecified')}
                    </p>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  {interviewStatus[selectedApplication.id]?.has_completed_interview ? (() => {
                    const appStatus = interviewStatus[selectedApplication.id];
                    const latestAttempt = appStatus?.attempts?.[0] || appStatus?.last_interview_result;
                    const latestDate = latestAttempt?.completed_at || latestAttempt?.ended_at || latestAttempt?.started_at;
                    const attemptsList = appStatus?.attempts?.length
                      ? appStatus.attempts
                      : [latestAttempt].filter(Boolean);

                    return (
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-2 font-medium">{t('myApplications.interview', 'Interview')}</p>
                          <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-200 space-y-4">
                            <div className="flex items-start gap-3">
                              <div className="p-2 bg-emerald-100 rounded-xl text-emerald-700 shrink-0">
                                <CheckCircle className="w-6 h-6" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-base font-bold text-emerald-900">
                                    {t('myApplications.interviewCompleted', 'Interview Completed')}
                                  </span>
                                  {attemptsList.length > 1 && (
                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-200 text-emerald-800">
                                      {attemptsList.length} {t('myApplications.attempts', 'Attempts')}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-emerald-800 mt-1 leading-relaxed">
                                  {latestDate ? (
                                    <>
                                      {t('myApplications.youCompletedInterviewOn', 'You completed your interview on')}{' '}
                                      <span className="font-semibold text-emerald-950">{formatDateTime(latestDate)}</span>.{' '}
                                      {t('myApplications.teamWillReviewSoon', 'Our team will review your responses and reach out to you soon.')}
                                    </>
                                  ) : (
                                    t(
                                      'myApplications.interviewCompletedNotice',
                                      'You have completed your interview. Our team will review your responses and reach out to you soon.'
                                    )
                                  )}
                                </p>
                              </div>
                            </div>

                            {/* Interview Attempts History */}
                            <div className="pt-3 border-t border-emerald-200/70 space-y-2">
                              <p className="text-xs font-bold text-emerald-900 uppercase tracking-wider">
                                {t('myApplications.interviewAttemptsHistory', 'Interview History')}
                              </p>
                              <div className="space-y-2">
                                {attemptsList.map((att, idx) => {
                                  const attemptNum =
                                    att.attempt_number ||
                                    (attemptsList.length - idx);
                                  const completedDate = att.completed_at || att.ended_at || att.started_at;
                                  const duration = formatDuration(att);

                                  return (
                                    <div
                                      key={att.session_id || idx}
                                      className="bg-white/90 rounded-xl p-3 border border-emerald-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 shadow-sm"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-800">
                                          {formatAttemptLabel(attemptNum)}
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 sm:justify-end">
                                        {completedDate && (
                                          <span className="flex items-center gap-1.5 font-medium text-gray-700">
                                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                            {formatDateTime(completedDate)}
                                          </span>
                                        )}
                                        {duration && (
                                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 font-medium">
                                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                                            {duration}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* If candidate also generated a new active interview link via plus icon */}
                        {interviewLinks[selectedApplication.id] && (
                          <div>
                            <p className="text-sm text-gray-600 mb-2 font-medium">{t('myApplications.newInterviewLink', 'New Interview Link')}</p>
                            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm font-semibold text-green-800">{t('myApplications.interviewReady')}</span>
                              </div>

                              <div className="space-y-3">
                                <a
                                  href={resolveInterviewUrl(interviewLinks[selectedApplication.id].url)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 w-full font-semibold"
                                >
                                  <Video className="w-4 h-4" />{t('myApplications.startInterview')}
                                </a>

                                <button
                                  onClick={() => copyInterviewLink(interviewLinks[selectedApplication.id].url, selectedApplication.job_title)}
                                  className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 w-full font-semibold"
                                >
                                  <Copy className="w-4 h-4" />{t('myApplications.copyInterviewLink')}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })() : interviewLinks[selectedApplication.id] ? (
                    <div>
                      <p className="text-sm text-gray-600 mb-2 font-medium">{t('myApplications.interviewLink')}</p>
                      <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-sm font-semibold text-green-800">{t('myApplications.interviewReady')}</span>
                        </div>

                        <div className="space-y-3">
                          <a
                            href={resolveInterviewUrl(interviewLinks[selectedApplication.id].url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 w-full font-semibold"
                          >
                            <Video className="w-4 h-4" />{t('myApplications.startInterview')}
                          </a>

                          <button
                            onClick={() => copyInterviewLink(interviewLinks[selectedApplication.id].url, selectedApplication.job_title)}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 w-full font-semibold"
                          >
                            <Copy className="w-4 h-4" />{t('myApplications.copyInterviewLink')}
                          </button>
                        </div>

                        {new Date(interviewLinks[selectedApplication.id].expiresAt) < new Date() && (
                          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg text-center">
                            <span className="text-sm text-red-600 font-medium">{t('myApplications.linkExpiredWarning')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600 mb-2 font-medium">{t('myApplications.interviewSession')}</p>
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <div className="flex items-center gap-2 mb-3">
                          <Clock className="w-4 h-4 text-gray-600" />
                          <span className="text-sm text-gray-700">{t('myApplications.noInterviewCreated')}</span>
                        </div>

                        <button
                          onClick={() => createInterviewSession(selectedApplication.id)}
                          disabled={creatingInterview === selectedApplication.id}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {creatingInterview === selectedApplication.id ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                          {t('myApplications.createInterviewSession')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="lg:col-span-2">
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-xl font-bold text-slate-800 mb-4">{t('myApplications.yourCoverLetter')}</h4>
                    <div className="bg-gray-50 rounded-xl p-6 max-h-48 overflow-y-auto border border-gray-200">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {selectedApplication.cover_letter || t('myApplications.noCoverLetter')}
                      </p>
                    </div>
                  </div>
                </div>

                {selectedApplication.feedback_to_candidate && (
                  <div className="lg:col-span-2">
                    <h4 className="text-xl font-bold text-slate-800 mb-4">{t('myApplications.feedbackFromCompany')}</h4>
                    <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                      <p className="text-blue-800 leading-relaxed whitespace-pre-wrap">
                        {selectedApplication.feedback_to_candidate}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-8 py-6 bg-gray-50 rounded-b-3xl">
            <button
              onClick={() => setDetailDialogOpen(false)}
              className="ml-auto flex px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-all"
            >
              {t('myApplications.close')}
            </button>
          </div>
        </Modal>

        {/* WITHDRAW MODAL */}
        <Modal
          isOpen={deleteDialogOpen}
          onClose={() => { setDeleteDialogOpen(false); setDeletingApplication(null); }}
          maxWidth="max-w-md"
        >
          <div className="px-8 py-6 bg-red-50/70 rounded-t-3xl">
            <div className="flex items-center gap-3">
              <LogOut className="w-8 h-8 text-red-600" />
              <h3 className="text-2xl font-bold text-red-800">{t('myApplications.withdrawApplication')}</h3>
            </div>
          </div>

          <div className="p-8">
            {deletingApplication && (
              <div className="space-y-4">
                <p className="text-gray-800 mb-4 text-center">
                  {t('myApplications.withdrawConfirm')}
                </p>

                <div className="bg-gray-50 rounded-xl p-4 space-y-2 border">
                  <p><span className="font-semibold">{t('myApplications.positionLabel')}</span> {deletingApplication.job_title}</p>
                  <p><span className="font-semibold">{t('myApplications.companyLabel')}</span> {deletingApplication.company_name}</p>
                  <p><span className="font-semibold">{t('myApplications.appliedLabel')}</span> {formatDate(deletingApplication.applied_at)}</p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      {t('myApplications.withdrawWarning')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-8 py-6 bg-gray-50 rounded-b-3xl flex justify-end gap-3">
            <button
              onClick={() => { setDeleteDialogOpen(false); setDeletingApplication(null); }}
              className="px-6 py-3 text-gray-700 font-semibold hover:bg-gray-200/70 rounded-xl transition-colors"
            >
              {t('myApplications.cancel')}
            </button>

            <button
              onClick={confirmDelete}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-all"
            >
              <LogOut className="w-4 h-4" />{t('myApplications.withdraw')}
            </button>
          </div>
        </Modal>

        {/* TOAST */}
        <Toast
          show={snackbar.open}
          message={snackbar.message}
          type={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
        </div>
      </PageCard>
    </PageShell>
  );
}
